import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type Lang = "en" | "af" | "zu" | "fr" | "de" | "es" | "pt";

export const LANGS: { code: Lang; label: string; native: string }[] = [
  { code: "en", label: "English", native: "English" },
  { code: "af", label: "Afrikaans", native: "Afrikaans" },
  { code: "zu", label: "isiZulu", native: "isiZulu" },
  { code: "fr", label: "French", native: "Français" },
  { code: "de", label: "German", native: "Deutsch" },
  { code: "es", label: "Spanish", native: "Español" },
  { code: "pt", label: "Portuguese", native: "Português" },
];

// Country (ISO-2) -> default language
const COUNTRY_LANG: Record<string, Lang> = {
  ZA: "en", NA: "af", BW: "en", ZW: "en", LS: "en", SZ: "en",
  // Major Zulu-speaking distribution → fallback English already covers
  FR: "fr", BE: "fr", LU: "fr", CH: "fr", CI: "fr", SN: "fr", CM: "fr", CD: "fr", MG: "fr",
  DE: "de", AT: "de",
  ES: "es", MX: "es", AR: "es", CL: "es", CO: "es", PE: "es", VE: "es", UY: "es", EC: "es",
  PT: "pt", BR: "pt", AO: "pt", MZ: "pt",
};

type Dict = Record<string, string>;
const DICT: Record<Lang, Dict> = {
  en: {
    "nav.services": "Services",
    "nav.portfolio": "Portfolio",
    "nav.packages": "Packages",
    "nav.process": "Process",
    "nav.faq": "FAQ",
    "nav.about": "About",
    "cta.whatsappUs": "WhatsApp Us",
    "cta.whatsappChat": "Chat on WhatsApp",
    "cta.whatsappChatWithUs": "Chat With Us on WhatsApp",
    "cta.freeAssessment": "Free Assessment",
    "cta.seeOurWork": "See Our Work",
    "hero.kicker": "Full-Service Branding Studio",
    "hero.title": "Branding that",
    "hero.titleHighlight": "wins customers.",
    "hero.subtitle": "BrandIn Studio is a South African branding studio crafting elite identities, websites, and WhatsApp Business systems for small businesses that want to look established — and convert enquiries into paying customers.",
    "hero.check1": "5–10 day delivery",
    "hero.check2": "Fixed pricing",
    "hero.check3": "WhatsApp-first",
    "final.title1": "Ready to Make Your Business Look",
    "final.titleAccent": "Professional",
    "final.subtitle": "Start with a free brand assessment. We'll review your current presence and show you exactly what to fix to attract more customers.",
    "final.replies": "Replies within business hours",
    "lang.label": "Language",
    "about.kicker": "About Us",
    "about.title": "Built for Businesses That Want to Be Taken Seriously",
    "about.p1": "BrandIn Studio helps entrepreneurs, service providers, contractors, and growing businesses package their businesses professionally so they can be trusted faster, taken seriously, and compete for better opportunities.",
    "about.p2": "We create the essential brand assets every serious business needs — logos, company profiles, business cards, email signatures, posters, Google Business content, and digital marketing material — not just to make the business look good, but to help it sell, present, quote, pitch, and grow with confidence.",
  },
  af: {
    "nav.services": "Dienste",
    "nav.portfolio": "Portefeulje",
    "nav.packages": "Pakkette",
    "nav.process": "Proses",
    "nav.faq": "Vrae",
    "nav.about": "Oor Ons",
    "cta.whatsappUs": "WhatsApp Ons",
    "cta.whatsappChat": "Gesels op WhatsApp",
    "cta.whatsappChatWithUs": "Gesels met ons op WhatsApp",
    "cta.freeAssessment": "Gratis Assessering",
    "cta.seeOurWork": "Sien Ons Werk",
    "hero.kicker": "Volledige Handelsmerk-ateljee",
    "hero.title": "Handelsmerke wat",
    "hero.titleHighlight": "kliënte wen.",
    "hero.subtitle": "BrandIn Studio is 'n Suid-Afrikaanse handelsmerk-ateljee wat elite identiteite, webwerwe en WhatsApp Business-stelsels bou vir klein besighede wat gevestig wil lyk — en navrae in betalende kliënte verander.",
    "hero.check1": "5–10 dae aflewering",
    "hero.check2": "Vaste pryse",
    "hero.check3": "WhatsApp-eerste",
    "final.title1": "Gereed om jou besigheid",
    "final.titleAccent": "Professioneel",
    "final.subtitle": "Begin met 'n gratis handelsmerk-assessering. Ons hersien jou huidige teenwoordigheid en wys jou presies wat om reg te stel om meer kliënte te lok.",
    "final.replies": "Antwoorde binne werksure",
    "lang.label": "Taal",
  },
  zu: {
    "nav.services": "Izinsizakalo",
    "nav.portfolio": "Iphothifoliyo",
    "nav.packages": "Amaphakeji",
    "nav.process": "Inqubo",
    "nav.faq": "Imibuzo",
    "nav.about": "Mayelana Nathi",
    "cta.whatsappUs": "Sithinte ku-WhatsApp",
    "cta.whatsappChat": "Xoxa ku-WhatsApp",
    "cta.whatsappChatWithUs": "Xoxa nathi ku-WhatsApp",
    "cta.freeAssessment": "Uhlolo Lwamahhala",
    "cta.seeOurWork": "Bheka Umsebenzi Wethu",
    "hero.kicker": "Isitudiyo Sokwakha Igama Lebhizinisi",
    "hero.title": "Ukwakha igama",
    "hero.titleHighlight": "okuthola amakhasimende.",
    "hero.subtitle": "I-BrandIn Studio iyisitudiyo saseNingizimu Afrika esikha izimpawu zekhwalithi, amawebhusayithi nezinhlelo ze-WhatsApp Business kumabhizinisi amancane afuna ukubukeka ezinzile — futhi aguqule imibuzo ibe ngamakhasimende akhokhayo.",
    "hero.check1": "Ukulethwa kwezinsuku ezi-5–10",
    "hero.check2": "Amanani amisiwe",
    "hero.check3": "I-WhatsApp kuqala",
    "final.title1": "Ukulungele Ukwenza Ibhizinisi Lakho Libukeke",
    "final.titleAccent": "Liphawulekayo",
    "final.subtitle": "Qala ngohlolo lwegama lwamahhala. Sizobheka ukubukeka kwakho kwamanje sikukhombise lokho okumele ukulungise ukuze uheha amakhasimende amaningi.",
    "final.replies": "Iziphendulo phakathi kwamahora omsebenzi",
    "lang.label": "Ulimi",
  },
  fr: {
    "nav.services": "Services",
    "nav.portfolio": "Portfolio",
    "nav.packages": "Forfaits",
    "nav.process": "Processus",
    "nav.faq": "FAQ",
    "nav.about": "À Propos",
    "cta.whatsappUs": "WhatsApp",
    "cta.whatsappChat": "Discuter sur WhatsApp",
    "cta.whatsappChatWithUs": "Discutez avec nous sur WhatsApp",
    "cta.freeAssessment": "Évaluation Gratuite",
    "cta.seeOurWork": "Voir Nos Réalisations",
    "hero.kicker": "Studio de Branding Complet",
    "hero.title": "Une image de marque qui",
    "hero.titleHighlight": "convertit vos clients.",
    "hero.subtitle": "BrandIn Studio est un studio sud-africain qui conçoit des identités haut de gamme, des sites web et des systèmes WhatsApp Business pour les PME qui veulent paraître établies — et transformer les demandes en clients payants.",
    "hero.check1": "Livraison en 5 à 10 jours",
    "hero.check2": "Tarifs fixes",
    "hero.check3": "WhatsApp d'abord",
    "final.title1": "Prêt à rendre votre entreprise",
    "final.titleAccent": "Professionnelle",
    "final.subtitle": "Commencez par une évaluation gratuite de votre marque. Nous examinons votre présence actuelle et vous montrons exactement quoi corriger pour attirer plus de clients.",
    "final.replies": "Réponses pendant les heures de bureau",
    "lang.label": "Langue",
  },
  de: {
    "nav.services": "Leistungen",
    "nav.portfolio": "Portfolio",
    "nav.packages": "Pakete",
    "nav.process": "Ablauf",
    "nav.faq": "FAQ",
    "nav.about": "Über Uns",
    "cta.whatsappUs": "WhatsApp",
    "cta.whatsappChat": "Auf WhatsApp chatten",
    "cta.whatsappChatWithUs": "Chatten Sie mit uns auf WhatsApp",
    "cta.freeAssessment": "Kostenlose Analyse",
    "cta.seeOurWork": "Unsere Arbeiten",
    "hero.kicker": "Full-Service Branding-Studio",
    "hero.title": "Branding, das",
    "hero.titleHighlight": "Kunden gewinnt.",
    "hero.subtitle": "BrandIn Studio ist ein südafrikanisches Branding-Studio, das erstklassige Identitäten, Websites und WhatsApp-Business-Systeme für kleine Unternehmen entwickelt, die etabliert wirken und Anfragen in zahlende Kunden verwandeln möchten.",
    "hero.check1": "Lieferung in 5–10 Tagen",
    "hero.check2": "Festpreise",
    "hero.check3": "WhatsApp zuerst",
    "final.title1": "Bereit, Ihr Unternehmen",
    "final.titleAccent": "professionell",
    "final.subtitle": "Starten Sie mit einer kostenlosen Markenanalyse. Wir prüfen Ihren aktuellen Auftritt und zeigen, was Sie konkret verbessern sollten, um mehr Kunden zu gewinnen.",
    "final.replies": "Antworten während der Geschäftszeiten",
    "lang.label": "Sprache",
  },
  es: {
    "nav.services": "Servicios",
    "nav.portfolio": "Portafolio",
    "nav.packages": "Paquetes",
    "nav.process": "Proceso",
    "nav.faq": "Preguntas",
    "nav.about": "Nosotros",
    "cta.whatsappUs": "WhatsApp",
    "cta.whatsappChat": "Chatear por WhatsApp",
    "cta.whatsappChatWithUs": "Chatea con nosotros en WhatsApp",
    "cta.freeAssessment": "Evaluación Gratis",
    "cta.seeOurWork": "Ver Nuestro Trabajo",
    "hero.kicker": "Estudio de Branding Integral",
    "hero.title": "Branding que",
    "hero.titleHighlight": "conquista clientes.",
    "hero.subtitle": "BrandIn Studio es un estudio sudafricano que crea identidades de élite, sitios web y sistemas de WhatsApp Business para pequeñas empresas que quieren verse establecidas — y convertir consultas en clientes que pagan.",
    "hero.check1": "Entrega en 5–10 días",
    "hero.check2": "Precios fijos",
    "hero.check3": "WhatsApp primero",
    "final.title1": "Listo para que tu negocio luzca",
    "final.titleAccent": "Profesional",
    "final.subtitle": "Comienza con una evaluación de marca gratuita. Revisamos tu presencia actual y te mostramos exactamente qué corregir para atraer más clientes.",
    "final.replies": "Respuestas en horario laboral",
    "lang.label": "Idioma",
  },
  pt: {
    "nav.services": "Serviços",
    "nav.portfolio": "Portfólio",
    "nav.packages": "Pacotes",
    "nav.process": "Processo",
    "nav.faq": "Perguntas",
    "nav.about": "Sobre",
    "cta.whatsappUs": "WhatsApp",
    "cta.whatsappChat": "Conversar no WhatsApp",
    "cta.whatsappChatWithUs": "Fale conosco no WhatsApp",
    "cta.freeAssessment": "Avaliação Gratuita",
    "cta.seeOurWork": "Ver Nosso Trabalho",
    "hero.kicker": "Estúdio de Branding Completo",
    "hero.title": "Branding que",
    "hero.titleHighlight": "conquista clientes.",
    "hero.subtitle": "BrandIn Studio é um estúdio sul-africano que cria identidades de elite, websites e sistemas de WhatsApp Business para pequenas empresas que querem parecer estabelecidas — e converter contatos em clientes pagantes.",
    "hero.check1": "Entrega em 5–10 dias",
    "hero.check2": "Preços fixos",
    "hero.check3": "WhatsApp primeiro",
    "final.title1": "Pronto para deixar seu negócio com cara",
    "final.titleAccent": "Profissional",
    "final.subtitle": "Comece com uma avaliação gratuita da sua marca. Analisamos sua presença atual e mostramos exatamente o que ajustar para atrair mais clientes.",
    "final.replies": "Respostas em horário comercial",
    "lang.label": "Idioma",
  },
};

type I18nState = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
};

const Ctx = createContext<I18nState>({ lang: "en", setLang: () => {}, t: (k) => k });

const STORAGE_KEY = "brandin_lang";

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>("en");

  useEffect(() => {
    let alive = true;
    (async () => {
      // Manual override wins
      const stored = (typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY)) as Lang | null;
      if (stored && DICT[stored]) {
        if (alive) setLangState(stored);
        return;
      }
      // Browser language hint
      const navLang = (typeof navigator !== "undefined" ? navigator.language.slice(0, 2).toLowerCase() : "") as Lang;
      if (navLang && DICT[navLang]) {
        if (alive) setLangState(navLang);
      }
      // Region from IP
      try {
        const cached = sessionStorage.getItem("brandin_country");
        let country = cached || "";
        if (!country) {
          const geo = await fetch("https://ipapi.co/json/").then((r) => (r.ok ? r.json() : null));
          country = String(geo?.country_code || "").toUpperCase();
          if (country) sessionStorage.setItem("brandin_country", country);
        }
        const mapped = COUNTRY_LANG[country];
        if (mapped && alive && !stored) setLangState(mapped);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<I18nState>(
    () => ({
      lang,
      setLang: (l) => {
        setLangState(l);
        try {
          localStorage.setItem(STORAGE_KEY, l);
        } catch {
          /* ignore */
        }
      },
      t: (key) => DICT[lang]?.[key] ?? DICT.en[key] ?? key,
    }),
    [lang],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  return useContext(Ctx);
}
